import "./index.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CompanyChangeRepository } from "@/api/company-change/repository";
import { CompanyChangeService } from "@/api/company-change/service";
import { ContractorsRepository } from "@/api/contractors/repository";
import { ContractorsService } from "@/api/contractors/service";
import { LoanOfficersRepository } from "@/api/loan-officers/repository";
import { LoanOfficersService } from "@/api/loan-officers/service";
import { LoanProcessorsRepository } from "@/api/loan-processors/repository";
import { LoanProcessorsService } from "@/api/loan-processors/service";
import Alert from "@/components/Alert";
import { ChangeCompanyDetails } from "@/components/ChangeCompany/ChangeCompanyDetails";
import { CompanyControl, TextareaControl } from "@/controls";
import { useCompanyDetails } from "@/hooks/useCompanyDetails";
import type { IContractingCompany } from "@/types/companies-contractors";
import type { ILender } from "@/types/companies-lenders";
import type { ICompanyChangeRequest } from "@/types/company-change-request";
import { RequestStatus } from "@/types/company-change-request";
import type { IUserProfile } from "@/types/users";
import { UserRole } from "@/types/users";
import { Button, Column, Modal, Toast } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IProps = {
  userDetails: IUserProfile;
  companyType: "contractor" | "lender";
  currentCompany: ILender | IContractingCompany;
  request: ICompanyChangeRequest;
  onDismiss: () => void;
  onSubmitSuccess: (request: ICompanyChangeRequest) => void;
};

type IFormDataType = {
  newCompanyId: string;
  adminResponse: string;
};

export const ChangeCompanyFormAdmin = ({
  userDetails,
  companyType,
  request,
  currentCompany,
  onDismiss,
  onSubmitSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const [rejectLoading, setRejectLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const {
    data: newCompany,
    loading: newCompanyLoading,
    getCompanyDetails: getNewCompanyDetails,
  } = useCompanyDetails();

  const companyChangeRepository = useMemo(() => {
    return new CompanyChangeRepository(new CompanyChangeService());
  }, []);

  useEffect(() => {
    if (!request.requestedCompanyId) {
      return;
    }

    void getNewCompanyDetails({
      id: request.requestedCompanyId,
      companyType,
    });
  }, [companyType, getNewCompanyDetails, request.requestedCompanyId]);

  const rejectSchema = useMemo(() => {
    return z.object({
      newCompanyId: z.string(),
      adminResponse: z.string().min(1, { message: t("validation.required") }),
    });
  }, [t]);

  const approveSchema = useMemo(() => {
    return z.object({
      newCompanyId: newCompany
        ? z.string()
        : z.string().min(1, { message: t("validation.required") }),
      adminResponse: z.string(),
    });
  }, [newCompany, t]);

  const methods = useForm<IFormDataType>({
    defaultValues: {
      adminResponse: "",
      newCompanyId: "",
    },
  });

  const { getValues } = methods;

  const validateWithSchema = useCallback(
    async (schema: z.ZodSchema<IFormDataType>) => {
      methods.clearErrors();
      const data = getValues();
      const result = schema.safeParse(data);

      if (!result.success) {
        result.error.issues.forEach((err) => {
          const field = err.path[0] as keyof IFormDataType;
          methods.setError(field, { type: "manual", message: err.message });
        });
        return { success: false, data };
      }

      return { success: true, data };
    },
    [getValues, methods],
  );

  const handleReject = useCallback(async () => {
    const { success, data } = await validateWithSchema(rejectSchema);
    if (!success) return;

    setRejectLoading(true);
    companyChangeRepository
      .processRequest({
        id: request.id,
        status: RequestStatus.Rejected,
        adminResponse: data.adminResponse,
      })
      .then((response) => {
        Toast.success({
          title: t("label.changeCompany.requestHasBeenRejected"),
        });
        onSubmitSuccess(response);
      })
      .catch(serverErrorHandler())
      .finally(() => {
        setRejectLoading(false);
      });
  }, [
    companyChangeRepository,
    validateWithSchema,
    rejectSchema,
    onSubmitSuccess,
    request.id,
    t,
  ]);

  const handleApprove = useCallback(async () => {
    const { success, data } = await validateWithSchema(approveSchema);
    if (!success) return;
    setApproveLoading(true);

    let promise: Promise<unknown> | undefined;

    if (userDetails.role === UserRole.Contractor) {
      const repository = new ContractorsRepository(new ContractorsService());
      promise = repository.updateProfile({
        id: userDetails.id,
        userId: userDetails.userId,
        contractingCompanyId:
          request.requestedCompanyId || Number(data.newCompanyId),
      });
    }

    if (userDetails.role === UserRole.LoanOfficer) {
      const repository = new LoanOfficersRepository(new LoanOfficersService());
      promise = repository.updateProfile({
        id: userDetails.id,
        userId: userDetails.userId,
        lenderId: request.requestedCompanyId || Number(data.newCompanyId),
      });
    }

    if (userDetails.role === UserRole.LoanProcessor) {
      const repository = new LoanProcessorsRepository(
        new LoanProcessorsService(),
      );
      promise = repository.updateProfile({
        id: userDetails.id,
        userId: userDetails.userId,
        lenderId: request.requestedCompanyId || Number(data.newCompanyId),
      });
    }

    if (!promise) {
      setApproveLoading(false);
      return;
    }

    promise
      .then(() => {
        Toast.success({
          title: t("label.changeCompany.companyHasBeenChanged"),
        });

        return companyChangeRepository.processRequest({
          id: request.id,
          status: RequestStatus.Approved,
        });
      })
      .then((response) => {
        onSubmitSuccess(response);
        onDismiss();
      })
      .catch(serverErrorHandler())
      .finally(() => {
        setApproveLoading(false);
      });
  }, [
    validateWithSchema,
    approveSchema,
    userDetails,
    request,
    t,
    companyChangeRepository,
    onSubmitSuccess,
    onDismiss,
  ]);

  return (
    <FormProvider {...methods}>
      <Modal
        open={true}
        onOpenChange={onDismiss}
        className="change-company__modal"
      >
        <Modal.Header title={t("label.changeCompany.label")} />
        <Modal.Body>
          <Column gap="md" fullWidth>
            <ChangeCompanyDetails
              request={request}
              currentCompany={currentCompany}
              newCompany={newCompany}
              newCompanyLoading={newCompanyLoading}
            />
            {!newCompanyLoading && !newCompany && (
              <Alert
                title={t("label.changeCompany.youNeedToAddNewCompanyToProceed")}
              />
            )}
            <CompanyControl name="newCompanyId" company={companyType} />
            <TextareaControl
              name="adminResponse"
              label={t("label.response")}
              placeholder={t("placeholder.writeYourResponse")}
              formElementClass="change-company__response-field"
            />
          </Column>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleReject}
            loading={rejectLoading}
            disabled={approveLoading}
            variant="ghost"
            color="danger"
          >
            {t("action.reject")}
          </Button>
          <Button
            onClick={handleApprove}
            loading={approveLoading}
            disabled={rejectLoading}
          >
            {t("action.approve")}
          </Button>
        </Modal.Footer>
      </Modal>
    </FormProvider>
  );
};
